import React, { useState, useEffect } from 'react';
import { PREDICT_RISK, SAVE_SURVEY } from '../constants/api';
import { MESSAGES } from '../constants/app';

interface Question {
  id: number;
  text: string;
  type: string;
  input_type: string;
  options?: {
    label: string;
    value: string;
  }[];
  units?: {
    label: string;
    value: string;
  }[];
}

const Survey: React.FC = () => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [filteredQuestions, setFilteredQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [textAnswer, setTextAnswer] = useState<string>('');
  const [numberAnswer, setNumberAnswer] = useState<string>('');
  const [selectedUnit, setSelectedUnit] = useState<string>('cm');
  const [feetValue, setFeetValue] = useState<string>('');
  const [inchesValue, setInchesValue] = useState<string>('');
  const [selectedCheckboxes, setSelectedCheckboxes] = useState<string[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [riskLevel, setRiskLevel] = useState<'high' | 'medium' | 'low'>('high');
  const [isLoading, setIsLoading] = useState(true);
  const [showQuestions, setShowQuestions] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [animatingOption, setAnimatingOption] = useState<string | null>(null);
  const [contactType, setContactType] = useState<'email' | 'mobile'>('email');
  const [contactInput, setContactInput] = useState<string>('');
  const [isApiLoading, setIsApiLoading] = useState(false);
  


  // Load questions from JSON file
  useEffect(() => {
    const loadQuestions = async () => {
      try {
        const response = await fetch('/surveyquestions.json');
        const data = await response.json();
        setQuestions(data);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading questions:', error);
        setIsLoading(false);
      }
    };

    loadQuestions();
  }, []);

  // Filter questions based on age and previous answers
  const filterQuestionsByAge = (allQuestions: Question[], age: string, answers: any) => {
    const ageNum = parseInt(age);
    if (isNaN(ageNum)) return allQuestions;
    
    return allQuestions.filter(question => {
      // Questions only for age >= 40
      if (question.type === 'abdominal_fat' || question.type === 'high_blood_pressure_or_cholesterol') {
        return ageNum >= 40;
      }
      
      // Questions only for age < 40
      if (question.type === 'family_hbp_cholesterol' || question.type === 'stress_level' || question.type === 'diet') {
        return ageNum < 40;
      }
      
      // Diabetes question only shows if high_blood_pressure_or_cholesterol is answered "yes"
      if (question.type === 'diabetes') {
        return ageNum >= 40 && answers.high_blood_pressure_or_cholesterol === 'yes';
      }
      
      // Sleep question only shows if exercise is answered "no_less_active"
      if (question.type === 'sleep') {
        return answers.exercise === 'no_less_active';
      }
      
      // Fatigue question only shows if sleep is answered "yes"
      if (question.type === 'fatigue_or_drowsiness') {
        return answers.exercise === 'no_less_active' && answers.sleep === 'yes';
      }
      
      // Survive emergency question only shows if heart_related_medical_emergency is answered "yes"
      if (question.type === 'survive_emergency') {
        return answers.heart_related_medical_emergency === 'yes';
      }
      
      return true;
    });
  };

  // Update filtered questions when age or relevant answers change
  useEffect(() => {
    if (questions.length > 0 && answers.age) {
      const filtered = filterQuestionsByAge(questions, answers.age, answers);
      setFilteredQuestions(filtered);
    } else if (questions.length > 0) {
      setFilteredQuestions(questions);
    }
  }, [questions, answers]);

  // Set default unit based on question type
  useEffect(() => {
    if (filteredQuestions.length > 0 && showQuestions) {
      const currentQuestion = filteredQuestions[currentQuestionIndex];
      if (currentQuestion?.input_type === 'unit_number') {
        if (currentQuestion.type === 'height') {
          setSelectedUnit('cm');
        } else if (currentQuestion.type === 'weight') {
          setSelectedUnit('kg');
        }
      }
    }
  }, [currentQuestionIndex, filteredQuestions, showQuestions]);

  // Restore form state when question changes (for proper back/forward navigation)
  useEffect(() => {
    if (filteredQuestions.length > 0 && showQuestions && currentQuestionIndex < filteredQuestions.length) {
      const currentQuestion = filteredQuestions[currentQuestionIndex];
      const savedAnswer = answers[currentQuestion.type] || '';
      
      // Reset all states first
      setSelectedAnswer('');
      setTextAnswer('');
      setNumberAnswer('');
      setSelectedUnit(currentQuestion.type === 'height' ? 'cm' : currentQuestion.type === 'weight' ? 'kg' : 'cm');
      setFeetValue('');
      setInchesValue('');
      setSelectedCheckboxes([]);
      setContactInput('');
      setContactType('email');
      
      // Restore appropriate state based on question type and input type
      if (savedAnswer) {
        if (currentQuestion.input_type === 'radio') {
          setSelectedAnswer(savedAnswer);
        } else if (currentQuestion.input_type === 'text') {
          if (currentQuestion.type === 'contact') {
            setContactInput(savedAnswer);
            const savedContactType = answers.contactType || 'email';
            setContactType(savedContactType as 'email' | 'mobile');
          } else {
            setTextAnswer(savedAnswer);
          }
        } else if (currentQuestion.input_type === 'number') {
          setNumberAnswer(savedAnswer);
        } else if (currentQuestion.input_type === 'unit_number') {
          if (savedAnswer.includes('ft') && savedAnswer.includes('in')) {
            const parts = savedAnswer.split(' ');
            setFeetValue(parts[0].replace('ft', ''));
            setInchesValue(parts[1].replace('in', ''));
            setSelectedUnit('feet_inches');
          } else if (savedAnswer) {
            const parts = savedAnswer.split(' ');
            if (parts.length >= 2) {
              setNumberAnswer(parts[0]);
              setSelectedUnit(parts[1]);
            }
          }
        } else if (currentQuestion.input_type === 'checkbox') {
          setSelectedCheckboxes(savedAnswer.split(', '));
        }
      }
    }
  }, [currentQuestionIndex, filteredQuestions, showQuestions, answers]);

  // Function to validate age input (max 2 digits)
  const handleAgeInput = (value: string) => {
    // Remove any non-numeric characters
    const numericValue = value.replace(/[^0-9]/g, '');
    // Limit to 2 digits
    if (numericValue.length <= 2) {
      setNumberAnswer(numericValue);
    }
  };

  // Function to validate weight input (max 3 digits)
  const handleWeightInput = (value: string) => {
    // Remove any non-numeric characters
    const numericValue = value.replace(/[^0-9]/g, '');
    // Limit to 3 digits
    if (numericValue.length <= 3) {
      setNumberAnswer(numericValue);
    }
  };

  // Function to validate feet input (max 2 digits)
  const handleFeetInput = (value: string) => {
    // Remove any non-numeric characters
    const numericValue = value.replace(/[^0-9]/g, '');
    // Limit to 2 digits
    if (numericValue.length <= 2) {
      setFeetValue(numericValue);
    }
  };

  // Function to validate inches input (max 2 digits)
  const handleInchesInput = (value: string) => {
    // Remove any non-numeric characters
    const numericValue = value.replace(/[^0-9]/g, '');
    // Limit to 2 digits
    if (numericValue.length <= 2) {
      setInchesValue(numericValue);
    }
  };

  // Function to validate height input based on unit
  const handleHeightInput = (value: string) => {
    // Remove any non-numeric characters
    const numericValue = value.replace(/[^0-9]/g, '');
    
    if (selectedUnit === 'cm') {
      // Limit to 3 digits for cm
      if (numericValue.length <= 3) {
        setNumberAnswer(numericValue);
      }
    } else {
      // For other units, use normal validation
      setNumberAnswer(numericValue);
    }
  };

  // Function to validate email
  const isValidEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // Function to validate Indian mobile number
  const isValidIndianMobile = (mobile: string) => {
    // Remove any non-numeric characters
    const cleanMobile = mobile.replace(/[^0-9]/g, '');
    // Indian mobile numbers: 10 digits starting with 6, 7, 8, or 9
    const mobileRegex = /^[6-9]\d{9}$/;
    return mobileRegex.test(cleanMobile);
  };

  // Function to handle contact input validation
  const handleContactInput = (value: string) => {
    if (contactType === 'mobile') {
      // For mobile, only allow digits and limit to 10 characters
      const numericValue = value.replace(/[^0-9]/g, '');
      if (numericValue.length <= 10) {
        setContactInput(numericValue);
      }
    } else {
      // For email, allow normal input
      setContactInput(value);
    }
  };

  // Function to handle contact type toggle
  const handleContactTypeToggle = (type: 'email' | 'mobile') => {
    setContactType(type);
    setContactInput(''); // Clear input when toggling
  };

  const handleAnswerSelect = (value: string) => {
    setSelectedAnswer(value);
    setAnimatingOption(value);
    
    // Auto-advance to next question for radio buttons after animation
    setTimeout(() => {
      setAnimatingOption(null);
      
      const currentQuestion = filteredQuestions[currentQuestionIndex];
      if (currentQuestion?.input_type === 'radio') {
        // Trigger the continue logic directly
        const currentAnswer = value;
        
        // Check if we have a valid answer
        if (currentAnswer && currentAnswer.trim() !== '') {
          // Save the current answer
          const newAnswers = {
            ...answers,
            [currentQuestion.type]: currentAnswer
          };
          setAnswers(newAnswers);

          // If this is a question that affects filtering, update filtered questions
          if (currentQuestion.type === 'age' || 
              currentQuestion.type === 'high_blood_pressure_or_cholesterol' ||
              currentQuestion.type === 'exercise' ||
              currentQuestion.type === 'sleep' ||
              currentQuestion.type === 'heart_related_medical_emergency') {
            const filtered = filterQuestionsByAge(questions, newAnswers.age || answers.age, newAnswers);
            setFilteredQuestions(filtered);
            // Move to next question in the filtered list
            if (currentQuestionIndex < filtered.length - 1) {
              setCurrentQuestionIndex(prev => prev + 1);
            } else {
              // Survey completed - just save the data, don't call API yet
              const formattedAnswers = createFormattedAnswers(newAnswers);
            }
          } else {
            // Move to next question or finish survey
            if (currentQuestionIndex < filteredQuestions.length - 1) {
              setCurrentQuestionIndex(prev => prev + 1);
            } else {
              // Survey completed - just save the data, don't call API yet
              const formattedAnswers = createFormattedAnswers(newAnswers);
            }
          }

          // Reset all answer states for next question
          setSelectedAnswer('');
          setTextAnswer('');
          setNumberAnswer('');
          setSelectedUnit('');
          setFeetValue('');
          setInchesValue('');
          setSelectedCheckboxes([]);
          setContactInput('');
        }
      }
    }, 600); // Match animation duration (0.3s × 3 repeats = 0.9s)
  };

  const handleCheckboxSelect = (value: string) => {
    setSelectedCheckboxes(prev => {
      if (value === 'none_of_the_above') {
        // If "None of the above" is selected, clear all other selections
        if (prev.includes('none_of_the_above')) {
          // If already selected, deselect it
          return prev.filter(item => item !== 'none_of_the_above');
        } else {
          // Select only "None of the above"
          return ['none_of_the_above'];
        }
      } else {
        // For other options, handle normal selection
        if (prev.includes(value)) {
          // Remove if already selected
          return prev.filter(item => item !== value);
        } else {
          // Add if not selected, but first remove "none_of_the_above" if it exists
          const filtered = prev.filter(item => item !== 'none_of_the_above');
          return [...filtered, value];
        }
      }
    });
  };

  // Function to create formatted answers with unit conversions
  const createFormattedAnswers = (answers: Record<string, string>) => {
    const formattedAnswers: Record<string, any> = {};
    let heightInCm: number | null = null;
    let weightInKg: number | null = null;
    
    Object.entries(answers).forEach(([key, value]) => {
      if (key === 'height') {
        // Convert height to cm
        if (value.includes('ft') && value.includes('in')) {
          // Parse feet and inches
          const parts = value.split(' ');
          const feet = parseFloat(parts[0].replace('ft', ''));
          const inches = parseFloat(parts[1].replace('in', ''));
          const totalInches = (feet * 12) + inches;
          const cm = Math.round(totalInches * 2.54);
          formattedAnswers[key] = cm;
          heightInCm = cm;
        } else {
          // Already in cm, just extract the number
          const cm = parseFloat(value.replace(' cm', ''));
          formattedAnswers[key] = cm;
          heightInCm = cm;
        }
      } else if (key === 'weight') {
        // Convert weight to kg
        if (value.includes('pounds')) {
          const pounds = parseFloat(value.replace(' pounds', ''));
          const kg = Math.round(pounds * 0.453592);
          formattedAnswers[key] = kg;
          weightInKg = kg;
        } else {
          // Already in kg, just extract the number
          const kg = parseFloat(value.replace(' kg', ''));
          formattedAnswers[key] = kg;
          weightInKg = kg;
        }
      } else if (key === 'contact') {
        // For contact field, include both the value and type
        formattedAnswers[key] = value;
        formattedAnswers.contactType = contactType;
      } else {
        // For all other fields, use the value as is
        formattedAnswers[key] = value;
      }
    });
    
    // Calculate BMI if both height and weight are available
    if (heightInCm !== null && weightInKg !== null) {
      const heightInMeters = heightInCm / 100;
      const bmi = parseFloat((weightInKg / (heightInMeters * heightInMeters)).toFixed(1));
      formattedAnswers.bmi = bmi;
    }
    
    return formattedAnswers;
  };

  // Function to map survey answers to API request format
  const mapSurveyToApiFormat = (answers: Record<string, any>) => {
    // Helper function to extract addiction values
    const hasAddiction = (type: string) => {
      return answers.addiction && answers.addiction.includes(type) ? 'Yes' : 'No';
    };

    // Map diet values
    const mapDiet = (diet: string) => {
      switch (diet) {
        case 'home_cooked_but_oily_food':
          return 'Home-cooked oily';
        case 'home_cooked_with_minimal_oil':
        case 'balanced_healthy_diet':
          return 'Home-cooked no oil';
        case 'mostly_junk_fried_oily_food':
          return 'Oily junk food';
        default:
          return 'Home-cooked no oil';
      }
    };

    // Map activity level
    const mapActivity = (exercise: string) => {
      switch (exercise) {
        case 'yes_regularly':
          return 'Yes';
        case 'occasionally':
          return 'Occasionally';
        case 'no_less_active':
          return 'No';
        default:
          return 'No';
      }
    };

    // Map yes/no answers
    const mapYesNo = (value: string) => {
      return value === 'yes' ? 'Yes' : 'No';
    };

    return {
      Age: parseInt(answers.age) || 25,
      Gender: answers.gender === 'male' ? 'Male' : 'Female',
      BMI: answers.bmi || 22.0,
      AbdominalWaistIncrease: mapYesNo(answers.abdominal_fat || 'no'),
      HighBP_or_Cholesterol: mapYesNo(answers.high_blood_pressure_or_cholesterol || 'no'),
      Diabetes: mapYesNo(answers.diabetes || 'no'),
      Family_BP_or_Cholesterol: mapYesNo(answers.family_hbp_cholesterol || 'no'),
      StressLevel: answers.stress_level === 'low' ? 'Low' : answers.stress_level === 'high' ? 'High' : 'Moderate',
      Diet: mapDiet(answers.diet || 'home_cooked_with_minimal_oil'),
      Smoking: hasAddiction('smoking'),
      ChewingTobacco: hasAddiction('chewing_tobacco'),
      Alcohol: hasAddiction('alcohol'),
      Activity: mapActivity(answers.exercise || 'no_less_active'),
      PhysicalActivity: mapActivity(answers.exercise || 'no_less_active'),
      EnoughSleep: mapYesNo(answers.sleep || 'no'),
      DaytimeFatigue: mapYesNo(answers.fatigue_or_drowsiness || 'no'),
      HeartEmergencyHistory: mapYesNo(answers.heart_related_medical_emergency || 'no')
    };
  };

  // Function to determine risk level from API response
  const determineRiskLevelFromScore = (score: number): 'high' | 'medium' | 'low' => {
    if (score <= 30) return 'low';
    else if (score <= 60) return 'medium';
    else return 'high';
  };

  // Function to call the risk prediction API
  const callRiskPredictionAPI = async (answers: Record<string, any>): Promise<'high' | 'medium' | 'low'> => {
    setIsApiLoading(true);
    let riskScore = 50; // Default fallback score
    let apiPayload;
    
    try {
      apiPayload = mapSurveyToApiFormat(answers);
      
      const response = await fetch(PREDICT_RISK, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiPayload)
      });

      if (!response.ok) {
        throw new Error(`API request failed with status: ${response.status}`);
      }

      const result = await response.json();
      
      riskScore = result.predicted_cvd_risk_score || 50;
      
    } catch (error) {
      console.error('Error calling risk prediction API:', error);
      riskScore = 50;
    }

    // Call survey save API regardless of predict API success/failure
    try {
      const surveyPayload = {
        response: {
          ...apiPayload,
          Name: answers.name || '',
          Contact: answers.contact || '',
          ContactType: answers.contactType || 'email'
        },
        risk_score: riskScore
      };
      
      
      const surveyResponse = await fetch(SAVE_SURVEY, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(surveyPayload)
      });

      if (!surveyResponse.ok) {
        console.warn(`Survey save API failed with status: ${surveyResponse.status}`);
      } else {
        await surveyResponse.json();
      }
    } catch (surveyError) {
      console.error('Error calling survey save API:', surveyError);
    }

    setIsApiLoading(false);
    return determineRiskLevelFromScore(riskScore);
  };

  const handleInitialContinue = () => {
    setShowQuestions(true);
  };

  // Function to handle Enter key press
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleContinue();
    }
  };

  const handleContinue = () => {
    const currentQuestion = filteredQuestions[currentQuestionIndex];
    let currentAnswer = '';

    // Get the current answer based on input type
    if (currentQuestion.input_type === 'radio') {
      currentAnswer = selectedAnswer;
    } else if (currentQuestion.input_type === 'text') {
      if (currentQuestion.type === 'contact') {
        currentAnswer = contactInput;
      } else {
        currentAnswer = textAnswer;
      }
    } else if (currentQuestion.input_type === 'number') {
      currentAnswer = numberAnswer;
    } else if (currentQuestion.input_type === 'unit_number') {
      if (selectedUnit === 'feet_inches') {
        currentAnswer = `${feetValue}ft ${inchesValue}in`;
      } else {
        currentAnswer = `${numberAnswer} ${selectedUnit}`;
      }
    } else if (currentQuestion.input_type === 'checkbox') {
      currentAnswer = selectedCheckboxes.join(', ');
    }

    // Check if we have a valid answer
    let isValidAnswer = false;
    
    if (currentQuestion.type === 'contact') {
      // Validate contact based on selected type
      if (contactType === 'email') {
        isValidAnswer = isValidEmail(contactInput.trim());
      } else if (contactType === 'mobile') {
        isValidAnswer = isValidIndianMobile(contactInput.trim());
      }
    } else if (currentQuestion.input_type === 'checkbox') {
      isValidAnswer = selectedCheckboxes.length > 0;
    } else {
      isValidAnswer = !!(currentAnswer && currentAnswer.trim() !== '');
    }

    if (isValidAnswer) {
      // Save the current answer
      const newAnswers = {
        ...answers,
        [currentQuestion.type]: currentAnswer
      };
      
      // Also save contact type if this is a contact question
      if (currentQuestion.type === 'contact') {
        newAnswers.contactType = contactType;
      }
      
      setAnswers(newAnswers);

      // If this is a question that affects filtering, update filtered questions
      if (currentQuestion.type === 'age' || 
          currentQuestion.type === 'high_blood_pressure_or_cholesterol' ||
          currentQuestion.type === 'exercise' ||
          currentQuestion.type === 'sleep' ||
          currentQuestion.type === 'heart_related_medical_emergency') {
        const filtered = filterQuestionsByAge(questions, newAnswers.age || answers.age, newAnswers);
        setFilteredQuestions(filtered);
        // Move to next question in the filtered list
        if (currentQuestionIndex < filtered.length - 1) {
          setCurrentQuestionIndex(prev => prev + 1);
          // Reset states for next question
          setSelectedAnswer('');
          setTextAnswer('');
          setNumberAnswer('');
          setSelectedUnit('cm');
          setFeetValue('');
          setInchesValue('');
          setSelectedCheckboxes([]);
          setContactInput('');
        } else {
          // Survey completed - create formatted JSON object and call API
          const formattedAnswers = createFormattedAnswers(newAnswers);

          
          // Call API to get risk level
          callRiskPredictionAPI(formattedAnswers).then(risk => {
            setRiskLevel(risk);
            setShowResults(true);
          });
        }
      } else {
        // Move to next question or finish survey
        if (currentQuestionIndex < filteredQuestions.length - 1) {
          setCurrentQuestionIndex(prev => prev + 1);
          // Reset states for next question
          setSelectedAnswer('');
          setTextAnswer('');
          setNumberAnswer('');
          setSelectedUnit('cm');
          setFeetValue('');
          setInchesValue('');
          setSelectedCheckboxes([]);
          setContactInput('');
        } else {
          // Survey completed - create formatted JSON object and call API
          const formattedAnswers = createFormattedAnswers(newAnswers);
          
          // Call API to get risk level
          callRiskPredictionAPI(formattedAnswers).then(risk => {
            setRiskLevel(risk);
            setShowResults(true);
          });
        }
      }
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      // State restoration is now handled by useEffect
    } else {
      // Go back to intro page if on first question
      setShowQuestions(false);
    }
  };

  const handleOkay = async () => {
    try {
      // Capture the risk UI as PNG image and download it
      await captureAndDownloadRiskImage();
      
      // Open native share screen with the image
      await shareRiskResult();
      
      // Reset survey state to go back to the beginning while staying on /#/survey
      setShowResults(false);
      setShowQuestions(false);
      setCurrentQuestionIndex(0);
      setAnswers({});
      setRiskLevel('high');
      setSelectedAnswer('');
      setTextAnswer('');
      setNumberAnswer('');
      setSelectedUnit('cm');
      setFeetValue('');
      setInchesValue('');
      setSelectedCheckboxes([]);
      setContactInput('');
      setContactType('email');
    } catch (error) {
      console.error('Error in download & share:', error);
      // Still reset the survey even if share fails
      setShowResults(false);
      setShowQuestions(false);
      setCurrentQuestionIndex(0);
      setAnswers({});
      setRiskLevel('high');
      setSelectedAnswer('');
      setTextAnswer('');
      setNumberAnswer('');
      setSelectedUnit('cm');
      setFeetValue('');
      setInchesValue('');
      setSelectedCheckboxes([]);
      setContactInput('');
      setContactType('email');
    }
  };

  // Function to capture the risk UI as PNG and download it
  const captureAndDownloadRiskImage = async () => {
    return new Promise<void>((resolve, reject) => {
      try {
        // Find the risk UI container
        const riskContainer = document.querySelector('[data-risk-container]') as HTMLElement;
        if (!riskContainer) {
          reject(new Error('Risk container not found'));
          return;
        }

        // Use html2canvas to capture the element as image
        import('html2canvas').then(html2canvas => {
          html2canvas.default(riskContainer, {
            backgroundColor: null,
            scale: 2, // Higher quality
            useCORS: true,
            allowTaint: true,
            width: riskContainer.offsetWidth,
            height: riskContainer.offsetHeight
          }).then(canvas => {
            // Convert canvas to blob
            canvas.toBlob((blob) => {
              if (!blob) {
                reject(new Error('Failed to create image blob'));
                return;
              }

              // Create download link
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `cardiovascular-risk-${riskLevel}-${new Date().toISOString().split('T')[0]}.png`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
              
              resolve();
            }, 'image/png');
          }).catch(reject);
        }).catch(reject);
      } catch (error) {
        reject(error);
      }
    });
  };

  // Function to share the risk result using native share API
  const shareRiskResult = async () => {
    try {
      if (navigator.share) {
        // First capture the image as a blob
        const imageBlob = await captureRiskImageAsBlob();
        const riskText = getRiskMessage();
        
        // Create a File object from the blob
        const imageFile = new File([imageBlob], `cardiovascular-risk-${riskLevel}-${new Date().toISOString().split('T')[0]}.png`, {
          type: 'image/png'
        });
        
        // Share the image file
        await navigator.share({
          title: 'Cardiovascular Risk Assessment',
          text: `My cardiovascular risk assessment result: ${riskText}`,
          files: [imageFile]
        });
      } else {
        // Fallback: copy to clipboard if share is not supported
        const riskText = getRiskMessage();
        const shareText = `My cardiovascular risk assessment result: ${riskText}\n\nTake your own assessment at: ${window.location.href}`;
        await navigator.clipboard.writeText(shareText);
        alert('Risk result copied to clipboard!');
      }
    } catch (error) {
      // Don't throw error for share cancellation
    }
  };

  // Function to capture the risk UI as a blob (for sharing)
  const captureRiskImageAsBlob = async (): Promise<Blob> => {
    return new Promise<Blob>((resolve, reject) => {
      try {
        // Find the risk UI container
        const riskContainer = document.querySelector('[data-risk-container]') as HTMLElement;
        if (!riskContainer) {
          reject(new Error('Risk container not found'));
          return;
        }

        // Use html2canvas to capture the element as image
        import('html2canvas').then(html2canvas => {
          html2canvas.default(riskContainer, {
            backgroundColor: null,
            scale: 2, // Higher quality
            useCORS: true,
            allowTaint: true,
            width: riskContainer.offsetWidth,
            height: riskContainer.offsetHeight
          }).then(canvas => {
            // Convert canvas to blob
            canvas.toBlob((blob) => {
              if (!blob) {
                reject(new Error('Failed to create image blob'));
                return;
              }
              resolve(blob);
            }, 'image/png');
          }).catch(reject);
        }).catch(reject);
      } catch (error) {
        reject(error);
      }
    });
  };

  // Function to get risk message based on current risk level
  const getRiskMessage = () => {
    switch (riskLevel) {
      case 'high':
        return 'High Risk - My heart health needs attention.';
      case 'medium':
        return 'Medium Risk - My heart health is moderate.';
      case 'low':
        return 'Low Risk - Great job! My heart health looks good.';
      default:
        return 'Risk assessment completed.';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading survey...</p>
        </div>
      </div>
    );
  }

  // Show intro page first
  if (!showQuestions) {
    return (
      <div className="min-h-screen bg-white flex flex-col relative">

        {/* Background Image */}
        <div 
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{ opacity: 0.2 }}
        >
          <img 
            src="/images/HeliusLogoWithText.svg" 
            alt="" 
            className="object-contain"
            style={{ width: '150px', height: '150px', marginTop: '225px' }}
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col px-6 py-4 relative z-10">

          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-black leading-tight">
              Cardiovascular Risk<br />
              Questionnaire (India)
            </h1>
          </div>

          {/* Description */}
          <div className="text-center mb-12 px-2">
            <p className="text-base text-gray-700 leading-relaxed">
              Answer a few simple questions to assess your heart health and get personalized recommendations for a healthier lifestyle.
            </p>
          </div>

          {/* Continue Button */}
          <div className="mt-auto mb-8">
            <button
              onClick={handleInitialContinue}
              className="w-full bg-blue-600 text-white font-normal py-4 px-6 rounded-2xl text-base"
            >
              Continue
            </button>
          </div>
        </div>

        {/* Bottom Home Indicator */}
        <div className="flex justify-center pb-2">
          <div className="w-32 h-1 bg-black rounded-full"></div>
        </div>
      </div>
    );
  }

  // Show results page
  if (showResults) {
    const getRiskConfig = () => {
      switch (riskLevel) {
        case 'high':
          return {
            bgColor: '#F43F5E',
            logo: '/images/High_logo.png',
            alt: 'High Risk',
            message1: 'Your heart health needs attention.',
            message2: "Let's improve it together!",
            buttonText: 'Download & Share'
          };
        case 'medium':
          return {
            bgColor: '#F59E0B',
            logo: '/images/Medium_logo.png',
            alt: 'Medium Risk',
            message1: 'Your heart health is moderate.',
            message2: 'Keep up the good work and stay vigilant!',
            buttonText: 'Download & Share'
          };
        case 'low':
          return {
            bgColor: '#14B8A6',
            logo: '/images/Low_logo.png',
            alt: 'Low Risk',
            message1: 'Great job! Your heart health looks good.',
            message2: 'Continue maintaining your healthy lifestyle!',
            buttonText: 'Download & Share'
          };
        default:
          return {
            bgColor: '#F43F5E',
            logo: '/images/High_logo.png',
            alt: 'High Risk',
            message1: 'Your heart health needs attention.',
            message2: "Let's improve it together!",
            buttonText: 'Download & Share'
          };
      }
    };

    const config = getRiskConfig();

    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: config.bgColor }} data-risk-container>
        {/* Header */}
        <div className="text-center py-6" style={{ backgroundColor: config.bgColor }}>
          <h1 className="text-2xl font-bold text-white">
            Cardiovascular Risk
          </h1>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-8">
          {/* Risk Logo */}
          <div className="mb-8">
            <img 
              src={config.logo} 
              alt={config.alt} 
              className="w-68 h-148"
            />
          </div>

          {/* Call to Action Text */}
          <div className="text-center mb-8">
            <p className="text-white text-lg mb-2">{config.message1}</p>
            <p className="text-white text-lg">{config.message2}</p>
          </div>

          {/* Download Button */}
          <button
            onClick={handleOkay}
            className="w-full bg-white py-4 px-6 rounded-2xl text-base font-medium"
            style={{ color: config.bgColor }}
          >
            {config.buttonText}
          </button>
        </div>

        {/* Bottom Home Indicator */}
        <div className="flex justify-center pb-2">
          <div className="w-32 h-1 bg-white rounded-full"></div>
        </div>
      </div>
    );
  }

  // Show questions page
  const currentQuestion = filteredQuestions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === filteredQuestions.length - 1;

  return (
    <div className="min-h-screen bg-white flex flex-col relative">

      {/* Background Image */}
      <div 
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        style={{ opacity: 0.2 }}
      >
        <img 
          src="/images/HeliusLogoWithText.svg" 
          alt="" 
          className="object-contain"
          style={{ width: '150px', height: '150px',marginTop: '225px' }}
        />
      </div>

      {/* Progress Bar - Top of Screen */}
      <div className="w-full bg-gray-200 h-2 relative z-10">
        <div 
          className="bg-blue-600 h-2 transition-all duration-300 ease-out"
          style={{ 
            width: `${((currentQuestionIndex + 1) / filteredQuestions.length) * 100}%` 
          }}
        ></div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col px-6 py-4 relative z-10" onKeyDown={handleKeyPress} tabIndex={0}>

        {/* Header with Back Button and Title */}
        <div className="flex items-center justify-center relative mb-10">
          <button
            onClick={handleBack}
            className="absolute left-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center"
          >
            <svg className="w-5 h-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <h1 className="text-xl font-bold leading-tight">
            Cardiovascular Risk
          </h1>
        </div>
        {/* Question */}
        <div>
          <h2 className="text-md font-bold text-black mb-3 leading-tight">
            {currentQuestion?.text}
          </h2>
        </div>

        {/* Answer Input */}
        <div className="mb-8">
          {currentQuestion?.input_type === 'text' && currentQuestion?.type !== 'contact' && (
            <input
              type="text"
              value={textAnswer}
              onChange={(e) => setTextAnswer(e.target.value)}
              placeholder={`Enter your ${currentQuestion.text.toLowerCase()}`}
              className="w-full p-4 border-2 border-gray-200 rounded-lg text-base focus:border-blue-500 focus:outline-none"
            />
          )}

          {currentQuestion?.input_type === 'text' && currentQuestion?.type === 'contact' && (
            <div className="space-y-4">
              {/* Contact Type Toggle */}
              <div className="flex items-center justify-center">
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => handleContactTypeToggle('email')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      contactType === 'email'
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    Email
                  </button>
                  <button
                    onClick={() => handleContactTypeToggle('mobile')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      contactType === 'mobile'
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    Mobile Number
                  </button>
                </div>
              </div>

              {/* Contact Input */}
              <div>
                <input
                  type={contactType === 'email' ? 'email' : 'tel'}
                  value={contactInput}
                  onChange={(e) => handleContactInput(e.target.value)}
                  placeholder={
                    contactType === 'email' 
                      ? 'Enter your email address' 
                      : 'Enter your mobile number'
                  }
                  className={`w-full p-4 border-2 rounded-lg text-base focus:outline-none ${
                    contactInput.trim() && (
                      (contactType === 'email' && !isValidEmail(contactInput.trim())) ||
                      (contactType === 'mobile' && !isValidIndianMobile(contactInput.trim()))
                    )
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-200 focus:border-blue-500'
                  }`}
                />
                
                {/* Validation Error Message */}
                {contactInput.trim() && (
                  (contactType === 'email' && !isValidEmail(contactInput.trim())) ||
                  (contactType === 'mobile' && !isValidIndianMobile(contactInput.trim()))
                ) && (
                  <p className="mt-2 text-sm text-red-500">
                    {contactType === 'email' 
                      ? 'Please enter a valid email address'
                      : 'Please enter a valid 10-digit Indian mobile number'
                    }
                  </p>
                )}
              </div>
            </div>
          )}
          
          {currentQuestion?.input_type === 'number' && (
            <input
              type="number"
              value={numberAnswer}
              onChange={(e) => {
                if (currentQuestion.type === 'age') {
                  handleAgeInput(e.target.value);
                } else {
                  setNumberAnswer(e.target.value);
                }
              }}
              placeholder={`Enter your ${currentQuestion.text.toLowerCase()}`}
              className="w-full p-4 border-2 border-gray-200 rounded-lg text-base focus:border-blue-500 focus:outline-none"
            />
          )}
          
          {currentQuestion?.input_type === 'unit_number' && (
            <div className="space-y-4">
              {/* Input Field with Toggle Button */}
              <div className="rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-gray-600">Select unit:</p>
                  <div className="flex bg-gray-200 rounded-lg p-1">
                    {currentQuestion.units?.map((unit, index) => (
                      <button
                        key={unit.value}
                        onClick={() => setSelectedUnit(unit.value)}
                        className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                          selectedUnit === unit.value
                            ? 'bg-blue-600 text-white'
                            : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                        }`}
                      >
                        {unit.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Input Fields */}
                {selectedUnit === 'feet_inches' ? (
                  <div className="flex space-x-3">
                    <div className="flex-1">
                      <input
                        type="number"
                        value={feetValue}
                        onChange={(e) => handleFeetInput(e.target.value)}
                        placeholder="Feet"
                        className="w-full p-3 border-2 border-gray-200 rounded-lg text-base focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                    <div className="flex-1">
                      <input
                        type="number"
                        value={inchesValue}
                        onChange={(e) => handleInchesInput(e.target.value)}
                        placeholder="Inches"
                        className="w-full p-3 border-2 border-gray-200 rounded-lg text-base focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <input
                      type="number"
                      value={numberAnswer}
                      onChange={(e) => {
                        if (currentQuestion.type === 'weight') {
                          handleWeightInput(e.target.value);
                        } else if (currentQuestion.type === 'height') {
                          handleHeightInput(e.target.value);
                        } else {
                          setNumberAnswer(e.target.value);
                        }
                      }}
                      placeholder={`Enter your ${currentQuestion.text.toLowerCase()}`}
                      className="w-full p-3 border-2 border-gray-200 rounded-lg text-base focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
          
          {currentQuestion?.input_type === 'radio' && currentQuestion?.options && (
            <div className="space-y-3">
              {currentQuestion.options.map((option, index) => (
                <button
                  key={option.value}
                  onClick={() => handleAnswerSelect(option.value)}
                  className={`w-full p-4 rounded-lg border-2 flex items-start space-x-3 text-left transition-colors ${
                    selectedAnswer === option.value
                      ? 'bg-blue-50 border-blue-500'
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  } ${
                    animatingOption === option.value ? 'animate-typeform-select' : ''
                  }`}
                >
                  {/* Radio Button */}
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    selectedAnswer === option.value
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-300 bg-white'
                  }`}>
                    {selectedAnswer === option.value && (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>
                  
                  {/* Option Text */}
                  <span className="text-black font-medium flex-1">
                    {option.label}
                  </span>
                </button>
              ))}
            </div>
          )}
          
          {currentQuestion?.input_type === 'checkbox' && currentQuestion?.options && (
            <div className="space-y-3">
              {currentQuestion.options.map((option, index) => {
                const isNoneOfTheAbove = option.value === 'none_of_the_above';
                const isNoneSelected = selectedCheckboxes.includes('none_of_the_above');
                const isOtherOptionSelected = !isNoneOfTheAbove && selectedCheckboxes.includes(option.value);
                const isDisabled = (isNoneOfTheAbove && isOtherOptionSelected) || (!isNoneOfTheAbove && isNoneSelected);
                
                return (
                  <button
                    key={option.value}
                    onClick={() => !isDisabled && handleCheckboxSelect(option.value)}
                    disabled={isDisabled}
                    className={`w-full p-4 rounded-lg border-2 flex items-center space-x-3 text-left transition-colors ${
                      isDisabled
                        ? 'bg-gray-100 border-gray-200 cursor-not-allowed opacity-50'
                        : selectedCheckboxes.includes(option.value)
                        ? 'bg-blue-50 border-blue-500'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    {/* Checkbox */}
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      selectedCheckboxes.includes(option.value)
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-300 bg-white'
                    }`}>
                      {selectedCheckboxes.includes(option.value) && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    
                    {/* Option Text */}
                    <span className={`font-medium ${
                      isDisabled ? 'text-gray-400' : 'text-black'
                    }`}>
                      {option.label}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Continue Button */}
        <div className="mt-auto mb-8">
          <button
            onClick={handleContinue}
            disabled={
              (currentQuestion?.input_type === 'radio' && !selectedAnswer) ||
              (currentQuestion?.input_type === 'text' && currentQuestion?.type !== 'contact' && !textAnswer.trim()) ||
              (currentQuestion?.input_type === 'text' && currentQuestion?.type === 'contact' && (
                !contactInput.trim() || 
                (contactType === 'email' && !isValidEmail(contactInput.trim())) ||
                (contactType === 'mobile' && !isValidIndianMobile(contactInput.trim()))
              )) ||
              (currentQuestion?.input_type === 'number' && !numberAnswer.trim()) ||
              (currentQuestion?.input_type === 'checkbox' && selectedCheckboxes.length === 0) ||
              (currentQuestion?.input_type === 'unit_number' && (
                !selectedUnit || 
                (selectedUnit === 'feet_inches' && (!feetValue.trim() || !inchesValue.trim())) ||
                (selectedUnit !== 'feet_inches' && !numberAnswer.trim())
              ))
            }
            className={`w-full py-4 px-6 rounded-2xl text-base font-medium transition-colors ${
              ((currentQuestion?.input_type === 'radio' && selectedAnswer) ||
               (currentQuestion?.input_type === 'text' && currentQuestion?.type !== 'contact' && textAnswer.trim()) ||
               (currentQuestion?.input_type === 'text' && currentQuestion?.type === 'contact' && contactInput.trim() && (
                 (contactType === 'email' && isValidEmail(contactInput.trim())) ||
                 (contactType === 'mobile' && isValidIndianMobile(contactInput.trim()))
               )) ||
               (currentQuestion?.input_type === 'number' && numberAnswer.trim()) ||
               (currentQuestion?.input_type === 'checkbox' && selectedCheckboxes.length > 0) ||
               (currentQuestion?.input_type === 'unit_number' && selectedUnit && (
                 (selectedUnit === 'feet_inches' && feetValue.trim() && inchesValue.trim()) ||
                 (selectedUnit !== 'feet_inches' && numberAnswer.trim())
               )))
                ? 'bg-blue-600 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isLastQuestion ? 'Complete Survey' : 'Continue'}
          </button>
        </div>
      </div>

      {/* Bottom Home Indicator */}
      <div className="flex justify-center pb-2">
        <div className="w-32 h-1 bg-black rounded-full"></div>
      </div>

      {/* API Loading Mask */}
      {isApiLoading && (
        <div className="fixed inset-0 bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 flex items-center justify-center z-50">
          <div className="text-center max-w-sm mx-auto px-6">
            {/* Modern Loading Animation */}
            <div className="relative mb-8">
              {/* Outer rotating ring */}
              <div className="animate-spin rounded-full h-20 w-20 border-4 border-white border-opacity-20 mx-auto"></div>
              {/* Inner pulsing dot */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-pulse bg-white rounded-full h-4 w-4"></div>
              </div>
              {/* Progress ring */}
              <div className="absolute inset-0 animate-spin rounded-full h-20 w-20 border-4 border-transparent border-t-white mx-auto" style={{ animationDuration: '2s' }}></div>
            </div>
            
            {/* Helius Logo */}
            <div className="mb-6">
              <img 
                src="/images/HeliusLogoWithText.svg" 
                alt="Helius" 
                className="w-16 h-16 mx-auto opacity-90"
              />
            </div>
            
            {/* Loading Text */}
            <div className="space-y-3">
              <h3 className="text-white text-xl font-semibold">Analyzing Your Health</h3>
              <div className="space-y-2">
                <p className="text-white text-sm opacity-90">{MESSAGES.LOADING.SURVEY_PROCESSING}</p>
                <div className="flex justify-center space-x-1">
                  <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Survey;
